import {
  BasePaginateParamsSchema,
  BasePaginateResultSchema,
} from '@/api/common.ts'
import dayjs from 'dayjs'
import { z } from 'zod'

export const Wo1210SearchParamsSchema = z
  .object({
    number: z
      .string()
      .optional()
      .transform((v) => v?.trim() || undefined),
    lotNo: z
      .string()
      .optional()
      .transform((v) => v?.trim() || undefined),
    itemId: z.string().optional(),
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional(),
    status: z
      .union([z.literal('1'), z.literal('2'), z.literal('3'), z.literal('4')])
      .optional(),
    mfgDate: z.coerce.date().optional().default(dayjs().toDate()),
    machine: z.string().optional(),
    additionalDirection: z.number().optional(),
  })
  .merge(BasePaginateParamsSchema)
  .transform((values) => ({
    ...values,
    mfgYear: values.mfgDate ? dayjs(values.mfgDate).isoWeekYear() : undefined,
    mfgWeek: values.mfgDate ? dayjs(values.mfgDate).isoWeek() : undefined,
  }))

export type Wo1210SearchParams = z.infer<typeof Wo1210SearchParamsSchema>

const holdInfoSchema = z
  .object({
    id: z.string(),
    date: z.coerce.date().nullable(),
    holdResultDate: z.coerce.date().nullable(),
    number: z.string(),
    qty: z.number(),
    taskId: z.number(),
    isSortingReworkApproved: z.boolean().nullable(),
    holdResultQtySortingRework: z.number().nullable(),
  })
  .transform((v) => ({
    ...v,
    isSortingReworkRequired:
      v.holdResultDate &&
      v.holdResultQtySortingRework != null &&
      v.holdResultQtySortingRework > 0,
  }))
  .transform((v) => ({
    ...v,
    isDone: v.isSortingReworkRequired
      ? v.isSortingReworkApproved != null
      : v.holdResultDate != null,
  }))

const Wo1210AddMaterialItemSchema = z.object({
  id: z.number(),
  direction: z.number(),
  items: z.array(
    z.object({
      item: z.object({
        itemId: z.number(),
        isClaimItem: z.boolean(),
        lotNo: z.string(),
        lotDate: z.coerce.date(),
        mold: z.string(),
        unit: z.string(),
      }),
      qty: z.number(),
    })
  ),
  pickingFulfilled: z.boolean(),
  pickingConfirmed: z.boolean(),
  pickingNumber: z.string().nullable(),
  pickingId: z.number().nullable(),
  requestId: z.string().nullable(),
})

const Wo1210RequiredMaterialsItemSchema = z.object({
  itemId: z.number(),
  isClaimItem: z.boolean().nullable(),
  lotNo: z.string().nullable(),
  mold: z.string().nullable(),
  qty: z.number(),
})

export type Wo1210RequiredMaterialsItemModel = z.infer<
  typeof Wo1210RequiredMaterialsItemSchema
>

const Wo1210RmPickingSchema = z.object({
  pickingId: z.number(),
  pickingNumber: z.string(),
  requestId: z.string().nullable(),
  source: z.string(),
  fulfilled: z.boolean(),
  confirmed: z.boolean(),
  cancelled: z.boolean(),
  usedSuggestion: z.boolean().nullable(),
  items: z.array(Wo1210RequiredMaterialsItemSchema),
})

export type Wo1210RmPickingModel = z.infer<typeof Wo1210RmPickingSchema>

export const Wo1210SearchResultModelSchema = z.object({
  id: z.number(),
  number: z.string(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  productionType: z.string(),
  machine: z.string(),
  partyId: z.number().nullable(),
  partyName: z.string().nullable(),
  lotNo: z.string().nullable(),
  itemId: z.number(),
  itemCode: z.string(),
  itemName: z.string(),
  qty: z.number(),
  requiredMaterials: z.array(Wo1210RequiredMaterialsItemSchema).nullable(),
  holdInfos: z.array(holdInfoSchema),
  additionalMaterials: z.array(Wo1210AddMaterialItemSchema),
  status: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  rmPickingFulfilled: z.boolean(),
  rmPickingConfirmed: z.boolean(),
  rmPickingNumber: z.string().nullable(),
  rmPickingId: z.number().nullable(),
  rmPickingUsedSuggestion: z.boolean().nullable(),
  hasRmBacklog: z.boolean(),
  rmBacklogItems: z.array(Wo1210RequiredMaterialsItemSchema).nullable(),
  rmPickings: z.array(Wo1210RmPickingSchema),
})

export type Wo1210SearchResultModel = z.infer<
  typeof Wo1210SearchResultModelSchema
>

export const Wo1210SearchResultSchema = z
  .object({
    data: z.array(Wo1210SearchResultModelSchema),
  })
  .merge(BasePaginateResultSchema)

export type Wo1210SearchResult = z.infer<typeof Wo1210SearchResultSchema>

export type Wo1210AdditionalMaterialItemModel = z.infer<
  typeof Wo1210AddMaterialItemSchema
>
